import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { RequestReplicateGgzoButton } from '../../../../elements/carePathButtons/RequestReplicateGgzoButton';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { DsmStoornis } from './DsmStoornis';
import { InterapyReportField } from './InterapyReportField';
import { Medicijnen } from './Medicijnen';

export function GgzoIntV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.findings')}
            />
            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <InterapyReportField
                    initValue={props.diagnosticReport.customControlData.init_risicoPsychose}
                    label={translateKey('interapy.riskOfPsychosis')}
                    value={props.diagnosticReport.customControlData.risicoPsychose}
                />

                <InterapyReportField
                    extraValueElement={
                        props.diagnosticReport.customControlData.medicijnen && (
                            <Medicijnen medicijnen={props.diagnosticReport.customControlData.medicijnen} />
                        )
                    }
                    initValue={props.diagnosticReport.customControlData.init_risicoMiddelen}
                    label={translateKey('interapy.riskOfDrugAbuse')}
                    value={props.diagnosticReport.customControlData.risicoMiddelen}
                />

                <InterapyReportField
                    initValue={props.diagnosticReport.customControlData.init_risicoSuicide}
                    label={translateKey('interapy.riskOfSuicide')}
                    value={props.diagnosticReport.customControlData.risicoSuicide}
                />

                <InterapyReportField
                    initValue={props.diagnosticReport.customControlData.init_complexiteit}
                    label={translateKey('interapy.comorbidity')}
                    remarks={props.diagnosticReport.customControlData.complexiteitToelichting}
                    value={props.diagnosticReport.customControlData.complexiteit}
                />

                <InterapyReportField
                    initValue={props.diagnosticReport.customControlData.init_ernst}
                    label={translateKey('interapy.seriousness')}
                    remarks={props.diagnosticReport.customControlData.ernstToelichting}
                    value={props.diagnosticReport.customControlData.ernst}
                />

                <DsmStoornis {...props.diagnosticReport.customControlData} />

                <InterapyReportField
                    initValue={props.diagnosticReport.customControlData.init_indicatieZorgzwaarte}
                    label={translateKey('interapy.indication')}
                    remarks={props.diagnosticReport.customControlData.indicatieToelichting}
                    value={props.diagnosticReport.customControlData.indicatieZorgzwaarte}
                />

                <DiagnosticReportReadButtons
                    {...props}
                    customButtonsLeft={[
                        <RequestReplicateGgzoButton
                            {...props}
                            questionnaireName={getServiceLabel(props.process.code, 'procedure', props.process.name)}
                        />,
                    ]}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
